
import type {
  ApiDashboardElement,
  ApiDashboardParametersType,
  ApiReturnProject,
  ApiReturnSite,
  AxesSelection,
  AxisJSON,
  Collection,
  EmptyPlotJSON,
  EmptyTableByCampaignJSON,
  EmptyTableJSON,
  EmptyTextJSON,
  Histogram,
  HistogramJSON,
  Image,
  ImageGallery,
  IntervalJSON,
  JSONSerializable,
  LocalizationFromCoordinates,
  LocalizationFromCoordinatesJSON,
  Map,
  MapJSON,
  Ombrothermic,
  OmbrothermicJSON,
  Plot,
  PlotConfigured,
  PlotJSON,
  RemoteWidget,
  RemoteWidgetJSON,
  ScatterPlot,
  ScatterPlotJSON,
  SelectionSchemaMeasurePoint,
  SelectionSchemaProjectSite,
  Source,
  SourceMap,
  Table,
  TableByCampaign,
  TableByCampaignConfigured,
  TableByCampaignJSON,
  TableConfigured,
  TableJSON,
  Text,
  TextConfigured,
  TextFromEntityProperty,
  TextFromEntityPropertyJSON,
  TextJSON,
  Timeseries,
  TimeseriesJSON,
  WidgetEntity,
  Windrose,
  WindroseJSON
} from '@highimpact-frontend/api-types';
import type { PlotTypes } from '@highimpact/dashboard-types';
import { getSourceLabelFromMap } from '../api/sourceLabelKeys';
import { flattenIndependentDimensionSources } from '../dashboard/widget/widgets/shared/widgetDimensionSlots';
import * as A from '../internal/utils/array';
import {
  addGuard,
  addIsomorphism,
  addTrigger,
  getModifierFromPath,
} from '../internal/utils/modifier';
import {
  validationAll,
  ValidationFunction,
} from './dashboard-element-validation';
import {
  areAllPathsWithMeasurePoint,
  areAllPathsWithProjectSite,
} from './sourcePathGuards';
import {
  findWidgetByDisplayerIdAcrossGrids,
  getDashboardSlice,
} from './store-operations';
import {
  isAggregatedColumn,
  makeEmptyAggregatedTable,
  updateDataSeriesColumnsE,
} from './table-element-operations';
import type { RootState } from './types';

// -- Constants

const defaultHeightAndWidth: Record<
  Exclude<ApiDashboardElement['element_type'], 'collection'>,
  { h: number; w: number }
> = {
  plot: { h: 12, w: 14 },
  map: { h: 6, w: 12 },
  table: { h: 14, w: 24 },
  tablebycampaign: { h: 14, w: 24 },
  text: { h: 3, w: 12 },
  textfromentityproperty: { h: 3, w: 12 },
  localizationfromcoordinates: { h: 6, w: 12 },
  image: { h: 10, w: 12 },
  remotewidget: { h: 12, w: 14 },
  readonlytext: { h: 3, w: 12 },
  imagegallery: { h: 14, w: 24 },
};

// -- Modifiers

type WithStartDateAndEndDate = PlotConfigured | TableByCampaignConfigured;
type WithDataSourceToAxis = PlotConfigured | TableByCampaignConfigured;
type WithIntervals =
  | Histogram
  | Timeseries
  | ScatterPlot
  | Windrose
  | Ombrothermic
  | TableConfigured;

const elementIdM = getModifierFromPath<ApiDashboardElement>()(['id']);
const elementNameM = getModifierFromPath<ApiDashboardElement>()(['name']);
const elementTypeM = getModifierFromPath<ApiDashboardElement>()([
  'element_type',
]);
const dataSourcesWithMeasurePointM = getModifierFromPath<WithMeasurePoint>()([
  'data_source',
]);
const dataSourcesWithProjectSiteM = getModifierFromPath<WithProjectSite>()([
  'data_source',
]);
const dataSourcesWithAnySelectionM = getModifierFromPath<WithAnySelection>()([
  'data_source',
]);
const dataSourceToAxisM = getModifierFromPath<WithDataSourceToAxis>()([
  'properties',
  'data_source_to_axis',
]);
const plotTypeM = getModifierFromPath<Plot>()(['properties', 'plot_type']);
const mapCoordinateSourceM = getModifierFromPath<Map>()([
  'properties',
  'coordinate_source',
]);
const entityPropertyM = getModifierFromPath<TextFromEntityProperty>()([
  'properties',
  'entity_property',
]);
const entityId = getModifierFromPath<TextFromEntityProperty>()([
  'properties',
  'entity_id',
]);
const entityType = getModifierFromPath<TextFromEntityProperty>()([
  'properties',
  'entity_type',
]);
const startDateM = getModifierFromPath<WithStartDateAndEndDate>()([
  'properties',
  'start_date',
]);
const endDateM = getModifierFromPath<WithStartDateAndEndDate>()([
  'properties',
  'end_date',
]);
const textValueM = getModifierFromPath<TextConfigured>()([
  'properties',
  'value',
]);
const specificWidgetTypeM = getModifierFromPath<RemoteWidget>()([
  'properties',
  'specific_widget_type',
]);
const urlM = getModifierFromPath<RemoteWidget>()(['properties', 'url']);
const additionalPropertiesM = getModifierFromPath<WithAdditionalProperties>()([
  'properties',
  'additional_properties',
]);
const elementsM = getModifierFromPath<Collection>()(['elements']);
const intervalsM = getModifierFromPath<WithIntervals>()([
  'properties',
  'intervals',
]);
export const isPlot = (element: ApiDashboardElement): element is Plot =>
  element.element_type === 'plot';
export const isConfiguredPlot = (
  element: ApiDashboardElement
): element is PlotConfigured =>
  isPlot(element) && element.properties.state_type === 'configured_plot';
const isMap = (element: ApiDashboardElement): element is Map =>
  element.element_type === 'map';
export const isTextFromEntityProperty = (
  element: ApiDashboardElement
): element is TextFromEntityProperty =>
  element.element_type === 'textfromentityproperty';
export const isTable = (element: ApiDashboardElement): element is Table =>
  element.element_type === 'table';
export const isConfiguredTable = (
  element: ApiDashboardElement
): element is TableConfigured =>
  isTable(element) && element.properties.state_type === 'configured_table';
export const isConfiguredText = (
  element: ApiDashboardElement
): element is TextConfigured =>
  isText(element) && element.properties.state_type === 'configured_Text';
export const isText = (element: ApiDashboardElement): element is Text =>
  element.element_type === 'text';
export const isTableByCampaign = (
  element: ApiDashboardElement
): element is TableByCampaign => element.element_type === 'tablebycampaign';
export const isConfiguredTableByCampaign = (
  element: ApiDashboardElement
): element is TableByCampaignConfigured =>
  isTableByCampaign(element) &&
  element.properties.state_type === 'configured_table_by_campaign';
export const isCollection = (
  element: ApiDashboardElement
): element is Collection => element.element_type === 'collection';
export const isHistogram = (
  element: ApiDashboardElement
): element is Histogram =>
  isConfiguredPlot(element) &&
  element.properties.plot_type === 'bar' &&
  'intervals' in element.properties;
export const isTimeseries = (
  element: ApiDashboardElement
): element is Timeseries =>
  isConfiguredPlot(element) &&
  element.properties.plot_type === 'timeseries' &&
  'intervals' in element.properties;
export const isScatterPlot = (
  element: ApiDashboardElement
): element is ScatterPlot =>
  isConfiguredPlot(element) &&
  element.properties.plot_type === 'scatter' &&
  'intervals' in element.properties;
export const isWindrose = (element: ApiDashboardElement): element is Windrose =>
  isConfiguredPlot(element) &&
  element.properties.plot_type === 'windrose' &&
  'intervals' in element.properties;
export const isOmbrothermic = (
  element: ApiDashboardElement
): element is Ombrothermic =>
  isConfiguredPlot(element) &&
  element.properties.plot_type === 'ombrothermic' &&
  'intervals' in element.properties;
export const isWithStartDateAndEndDate = (
  element: ApiDashboardElement
): element is WithStartDateAndEndDate =>
  isTableByCampaign(element) ||
  (isConfiguredPlot(element) && !isHistogram(element));
export const isWithIntervals = (
  element: ApiDashboardElement
): element is WithIntervals =>
  isConfiguredTable(element) ||
  isHistogram(element) ||
  isTimeseries(element) ||
  isScatterPlot(element) ||
  isWindrose(element) ||
  isOmbrothermic(element);

export const hasAdditionalProperties = (
  element: ApiDashboardElement
): element is WithAdditionalProperties =>
  isRemoteWidget(element) || isScatterPlot(element) || isTimeseries(element);
export type WithAdditionalProperties = RemoteWidget | ScatterPlot | Timeseries;

export const isWithDataSourceToAxis = (
  element: ApiDashboardElement
): element is WithDataSourceToAxis =>
  isTableByCampaign(element) || isPlot(element);
export const isRemoteWidget = (
  element: ApiDashboardElement
): element is RemoteWidget => element.element_type === 'remotewidget';
export type WithMeasurePoint = Plot | Table | TableByCampaign;
export const isWithMeasurePoint = (
  element: ApiDashboardElement
): element is WithMeasurePoint => {
  return isPlot(element) || isTable(element) || isTableByCampaign(element);
};
export type WithProjectSite =
  | Image
  | ImageGallery
  | TextFromEntityProperty
  | LocalizationFromCoordinates
  | Map;
export const isWithProjectSite = (
  element: ApiDashboardElement
): element is WithProjectSite => {
  return (
    element.element_type === 'image' ||
    element.element_type === 'imagegallery' ||
    isTextFromEntityProperty(element) ||
    isMap(element) ||
    element.element_type === 'localizationfromcoordinates'
  );
};
export type WithAnySelection = RemoteWidget | Text | Collection;
export const isWithAnySelection = (
  element: ApiDashboardElement
): element is WithAnySelection => {
  return !isWithMeasurePoint(element) && !isWithProjectSite(element);
};
const guardedTextValueM = addGuard(isConfiguredText, textValueM);
const guardedDataSourcesM = addGuard(
  isWithDataSourceToAxis,
  dataSourcesWithMeasurePointM
);
const guardedDataSourceToAxisM = addGuard(
  isWithDataSourceToAxis,
  dataSourceToAxisM
);
const guardedPlotTypeM = addGuard(isPlot, plotTypeM);
const guardedCoordinateSourceM = addGuard(isMap, mapCoordinateSourceM);
const guardedEntityPropertyM = addGuard(
  isTextFromEntityProperty,
  entityPropertyM
);
const guardedEntityId = addGuard(isTextFromEntityProperty, entityId);
const guardedEntityType = addGuard(isTextFromEntityProperty, entityType);
const guardedStartDateM = addGuard(isWithStartDateAndEndDate, startDateM);
const guardedEndDateM = addGuard(isWithStartDateAndEndDate, endDateM);
const guardedSpecificWidgetTypeM = addGuard(
  isRemoteWidget,
  specificWidgetTypeM
);
const guardedUrlM = addGuard(isRemoteWidget, urlM);
const guardedAdditionalPropertiesM = addGuard(
  hasAdditionalProperties,
  additionalPropertiesM
);
const guardedElementsM = addGuard(isCollection, elementsM);
const normalizedIntervalsM = addIsomorphism(
  intervalsM,
  (intervals) => intervals ?? [],
  (intervals) => intervals
);
const guardedIntervalsM = addGuard(isWithIntervals, normalizedIntervalsM);

// -- Constructors

export const makeDataSource = (
  p: ApiDashboardElement['data_source'][0]
): ApiDashboardElement['data_source'][0] => p;

export const makeAxis = (p: AxisJSON): AxisJSON => p;

interface MakeEmptyPlotConfig {
  plot_type: Plot['properties']['plot_type'];
  start_date: string;
  end_date: string;
}

interface MakeEmptyTableConfig {
  start_date: string;
  end_date: string;
}

const makeEmptyPlotProperties = (p: MakeEmptyPlotConfig): PlotJSON => ({
  state_type: 'configured_plot',
  plot_type: p.plot_type,
  data_source_to_axis: {},
  start_date: p.start_date,
  end_date: p.end_date,
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.plot.w,
  h: defaultHeightAndWidth.plot.h,
});

const makeEmptyHistogramProperties = (
  p: MakeEmptyPlotConfig
): HistogramJSON => ({
  state_type: 'configured_plot',
  plot_type: 'bar',
  data_source_to_axis: {},
  intervals: [
    {
      type: 'fixed_time_range',
      name: '',
      campaign_ids: null,
      startdate: p.start_date,
      enddate: p.end_date,
    },
  ],

  x: 0,
  y: 0,
  w: defaultHeightAndWidth.plot.w,
  h: defaultHeightAndWidth.plot.h,
});

const makeEmptyTimeseriesProperties = (
  p: MakeEmptyPlotConfig
): TimeseriesJSON => ({
  ...makeEmptyHistogramProperties(p),
  plot_type: 'timeseries',
});
const makeEmptyScatterPlotProperties = (
  p: MakeEmptyPlotConfig
): ScatterPlotJSON => ({
  ...makeEmptyHistogramProperties(p),
  plot_type: 'scatter',
});
const makeEmptyWindroseProperties = (p: MakeEmptyPlotConfig): WindroseJSON => ({
  ...makeEmptyHistogramProperties(p),
  plot_type: 'windrose',
});
const makeEmptyOmbrothermicProperties = (
  p: MakeEmptyPlotConfig
): OmbrothermicJSON => ({
  ...makeEmptyHistogramProperties(p),
  plot_type: 'ombrothermic',
});

const makeUnconfiguredPlotProperties = (): EmptyPlotJSON => ({
  state_type: 'unconfigured_plot',
  plot_type: 'timeseries',
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.plot.w,
  h: defaultHeightAndWidth.plot.h,
});

export const makeEmptyPlot = (
  p: MakeEmptyPlotConfig,
  unconfigured = false
): Plot => ({
  id: 0,
  name: '',
  element_type: 'plot',
  data_source: [],
  properties: unconfigured
    ? makeUnconfiguredPlotProperties()
    : p.plot_type === 'bar'
    ? makeEmptyHistogramProperties(p)
    : p.plot_type === 'timeseries'
    ? makeEmptyTimeseriesProperties(p)
    : p.plot_type === 'scatter'
    ? makeEmptyScatterPlotProperties(p)
    : p.plot_type === 'windrose'
    ? makeEmptyWindroseProperties(p)
    : p.plot_type === 'ombrothermic'
    ? makeEmptyOmbrothermicProperties(p)
    : makeEmptyPlotProperties(p),
  resource_ids: null,
});

const makeEmptyMapProperties = (coordinate_source: 'site'): MapJSON => ({
  coordinate_source,
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.map.w,
  h: defaultHeightAndWidth.map.h,
});

export const makeEmptyMap = (coordinate_source: 'site'): Map => ({
  id: 0,
  name: '',
  element_type: 'map',
  data_source: [],
  properties: makeEmptyMapProperties(coordinate_source),
  resource_ids: null,
});

const makeEmptyTableProperties = (p: MakeEmptyTableConfig): TableJSON => ({
  state_type: 'configured_table',
  config: makeEmptyAggregatedTable(),
  intervals: [
    {
      type: 'fixed_time_range',
      name: '',
      campaign_ids: null,
      startdate: p.start_date,
      enddate: p.end_date,
    },
  ],
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.plot.w,
  h: defaultHeightAndWidth.plot.h,
});

const makeUnconfiguredTableProperties = (
  p: MakeEmptyTableConfig
): EmptyTableJSON => ({
  state_type: 'unconfigured_table',
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.plot.w,
  h: defaultHeightAndWidth.plot.h,
});

export const makeEmptyTable = (
  p: MakeEmptyTableConfig,
  unconfigured = false
): Table => ({
  id: 0,
  name: '',
  element_type: 'table',
  data_source: [],
  properties: unconfigured
    ? makeUnconfiguredTableProperties(p)
    : makeEmptyTableProperties(p),
  resource_ids: null,
});

interface MakeEmptyTableByCampaignConfig {
  start_date: string;
  end_date: string;
}

export const makeEmptyTableByCampaignProperties = (
  p: MakeEmptyTableConfig
): TableByCampaignJSON => ({
  start_date: p.start_date,
  end_date: p.end_date,
  state_type: 'configured_table_by_campaign',
  data_source_to_axis: {},
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.tablebycampaign.w,
  h: defaultHeightAndWidth.tablebycampaign.h,
});

export const makeUnconfiguredTableByCampaignProperties =
  (): EmptyTableByCampaignJSON => ({
    state_type: 'unconfigured_table_by_campaign',
    x: 0,
    y: 0,
    w: defaultHeightAndWidth.plot.w,
    h: defaultHeightAndWidth.plot.h,
  });

export const makeEmptyTableByCampaign = (
  p: MakeEmptyTableByCampaignConfig,
  unconfigured = false
): TableByCampaign => ({
  id: 0,
  name: '',
  data_source: [],
  element_type: 'tablebycampaign',
  properties: unconfigured
    ? makeUnconfiguredTableByCampaignProperties()
    : makeEmptyTableByCampaignProperties(p),
});

const makeUnconfiguredTextProperties = (): EmptyTextJSON => ({
  state_type: 'unconfigured_Text',
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.text.w,
  h: defaultHeightAndWidth.text.h,
});

const makeEmptyTextProperties = (): TextJSON => ({
  state_type: 'configured_Text',
  value: '',
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.text.w,
  h: defaultHeightAndWidth.text.h,
});

export const makeEmptyText = (unconfigured = false): Text => ({
  id: 0,
  name: '',
  element_type: 'text',
  data_source: [],
  properties: unconfigured
    ? makeUnconfiguredTextProperties()
    : makeEmptyTextProperties(),
  resource_ids: null,
});

interface MakeTextFromEntityPropertyConfig {
  entity_type: TextFromEntityProperty['properties']['entity_type'];
  entity_id: TextFromEntityProperty['properties']['entity_id'];
  entity_property: TextFromEntityProperty['properties']['entity_property'];
}

const makeEmptyTextFromEntityPropertyProperties = (
  p: MakeTextFromEntityPropertyConfig
): TextFromEntityPropertyJSON => ({
  entity_type: p.entity_type,
  entity_id: p.entity_id,
  entity_property: p.entity_property,
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.textfromentityproperty.w,
  h: defaultHeightAndWidth.textfromentityproperty.h,
});

export const makeEmptyTextFromEntityProperty = (
  p: MakeTextFromEntityPropertyConfig
): TextFromEntityProperty => ({
  id: 0,
  name: '',
  element_type: 'textfromentityproperty',
  data_source: [],
  properties: makeEmptyTextFromEntityPropertyProperties(p),
  resource_ids: null,
});

interface MakeEmptyLocalizationFromCoordinatesConfig {
  coordinate_source: LocalizationFromCoordinates['properties']['coordinate_source'];
}

const makeEmptyLocalizationFromCoordinatesProperties = (
  p: MakeEmptyLocalizationFromCoordinatesConfig
): LocalizationFromCoordinatesJSON => ({
  coordinate_source: p.coordinate_source,
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.localizationfromcoordinates.w,
  h: defaultHeightAndWidth.localizationfromcoordinates.h,
});

export const makeEmptyLocalizationFromCoordinates = (
  p: MakeEmptyLocalizationFromCoordinatesConfig
): LocalizationFromCoordinates => ({
  id: 0,
  name: '',
  element_type: 'localizationfromcoordinates',
  data_source: [],
  properties: makeEmptyLocalizationFromCoordinatesProperties(p),
  resource_ids: null,
});

export const makeEmptyImageProperties = () => {
  return { x: 0, y: 0, w: 12, h: 6 };
};

export const makeEmptyImage = (): Image => ({
  id: 0,
  name: '',
  element_type: 'image',
  data_source: [],
  properties: {
    x: 0,
    y: 0,
    w: 12,
    h: 6,
  },
  resource_ids: [],
});

export const makeImageWithResource = (resource_id: number): Image => ({
  id: 0,
  name: '',
  element_type: 'image',
  data_source: [],
  properties: {
    x: 0,
    y: 0,
    w: 12,
    h: 6,
  },
  resource_ids: [resource_id],
});

export const makeEmptyImageGalleryProperties =
  (): ImageGallery['properties'] => {
    return {
      x: 0,
      y: 0,
      w: 12,
      h: 6,
      tags: [],
    };
  };

export const makeEmptyImageGallery = (): ImageGallery => ({
  id: 0,
  name: '',
  element_type: 'imagegallery',
  data_source: [],
  properties: {
    tags: [],
    x: 0,
    y: 0,
    w: 12,
    h: 6,
  },
  resource_ids: [],
});

const makeEmptyRemoteWidgetProperties = (
  specificWidgetType: string,
  url: string
): RemoteWidgetJSON => ({
  x: 0,
  y: 0,
  w: defaultHeightAndWidth.remotewidget.w,
  h: defaultHeightAndWidth.remotewidget.h,
  specific_widget_type: specificWidgetType,
  additional_properties: null,
  url,
});

export const makeEmptyRemoteWidget = (
  specificWidgetType: string,
  url: string
): RemoteWidget => ({
  id: 0,
  name: '',
  element_type: 'remotewidget',
  data_source: [],
  properties: makeEmptyRemoteWidgetProperties(specificWidgetType, url),
  resource_ids: null,
});

const makeEmptyCollectionProperties = (): Collection['properties'] => ({});

export const makeEmptyCollection = (): Collection => ({
  id: 0,
  name: '',
  data_source: [],
  element_type: 'collection',
  properties: makeEmptyCollectionProperties(),
  elements: [],
  resource_ids: null,
});

// -- Element type switchers

/**
 * These functions are used to switch between different element types, trying to keep as much of the original data as possible.
 */

export interface MeasureTypeIdToUnit {
  [key: number]: string;
}

interface ToPlotConfig {
  start_date: string;
  end_date: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ToTableConfig extends ToPlotConfig {}

// -- for compatibility with new source selector

export const makeDataSourceToAxisFromNewDataSources = (
  dataSources: Source[],
  settings: {
    lastIsX?: boolean;
  }
): PlotJSON['data_source_to_axis'] => {
  const axisByDataSourceKey: Record<number, AxisJSON> = {};
  dataSources.forEach((ds, i, arr) => {
    const axis_type = settings.lastIsX && i === arr.length - 1 ? 'x' : 'y';
    const unitIsUnique = !arr.some(
      (otherDs, otherIndex) =>
        otherIndex !== i && otherDs.measurement_unit === ds.measurement_unit
    );
    const axisName = unitIsUnique
      ? ds.label
      : `${ds.physical_quantity} (${ds.measurement_unit})`;
    const axis: AxisJSON = {
      axisName,
      start: 0,
      end: 5000,
      axis_type,
      unit: ds.measurement_unit,
    };
    axisByDataSourceKey[i] = axis;
    return ds;
  });
  return axisByDataSourceKey;
};

/**
 * Writes resolved catalog sources onto a measure-point widget element.
 */
export function setElementDataSourcesFromResolvedSources(
  sources: readonly Source[],
  element: ApiDashboardElement,
  options: { lastIsX?: boolean } = {}
): ApiDashboardElement {
  if (!isWithMeasurePoint(element)) {
    throw new Error(
      `Cannot set measure-point sources on element type ${element.element_type}`
    );
  }

  const selectionPaths: SelectionSchemaMeasurePoint[] = sources.map(
    (source) => ({
      measure_point_id: source.measure_point_id,
      measure_type_id: source.measure_type_id,
    })
  );
  const withDataSources = dataSourcesWithMeasurePointM.set(
    selectionPaths,
    element
  );

  if (!isWithDataSourceToAxis(withDataSources)) {
    return withDataSources;
  }

  const newAxes = makeDataSourceToAxisFromNewDataSources([...sources], options);
  return guardedDataSourceToAxisM.set(newAxes, withDataSources);
}

/**
 * Atomically writes a coupled dimension row onto a measure-point widget element.
 *
 * All slots in `coupledDimensionOrder` must be present in `sources` unless the
 * selection is being cleared.
 */
export function setElementCoupledSourcesFromResolvedSources(
  sources: readonly Source[],
  element: ApiDashboardElement,
  coupledDimensionOrder: readonly string[],
  options: { lastIsX?: boolean } = {}
): ApiDashboardElement {
  if (
    sources.length > 0 &&
    sources.length !== coupledDimensionOrder.length
  ) {
    throw new Error(
      `Expected ${coupledDimensionOrder.length} coupled sources, received ${sources.length}`
    );
  }

  return setElementDataSourcesFromResolvedSources(sources, element, options);
}

/**
 * Writes a scoped project-site entity onto entity-bound widget elements.
 */
export function setElementEntityScopeFromWidgetEntity(
  entity: WidgetEntity | null,
  element: ApiDashboardElement
): ApiDashboardElement {
  if (!isWithProjectSite(element)) {
    throw new Error(
      `Cannot set entity scope on element type ${element.element_type}`
    );
  }

  if (!entity) {
    return dataSourcesWithProjectSiteM.set([], element);
  }

  const selection: SelectionSchemaProjectSite[] = [
    {
      project_id: entity.project_id,
      site_id: entity.site_id,
      group_index: 0,
    },
  ];

  return dataSourcesWithProjectSiteM.set(selection, element);
}

/**
 * Flattens an independent-dimension selection map and writes it to the element.
 */
export function setElementIndependentSourcesFromResolvedSources(
  selection: Readonly<Record<string, readonly Source[]>>,
  dimensionOrder: readonly string[],
  element: ApiDashboardElement,
  options: { lastIsX?: boolean } = {}
): ApiDashboardElement {
  const sources = flattenIndependentDimensionSources(selection, dimensionOrder);

  return setElementDataSourcesFromResolvedSources(sources, element, options);
}

const toConfiguredPlotProperties = (
  p: MakeEmptyPlotConfig,
  element: ApiDashboardElement
): PlotJSON => ({
  ...makeEmptyPlotProperties(p),
  x: element.properties.x as number,
  y: element.properties.y as number,
  w: element.properties.w as number,
  h: element.properties.h as number,
});

const toConfiguredTableProperties = (
  p: MakeEmptyTableConfig,
  element: ApiDashboardElement
): TableJSON => ({
  ...makeEmptyTableProperties(p),
  x: element.properties.x as number,
  y: element.properties.y as number,
  w: element.properties.w as number,
  h: element.properties.h as number,
});

const toConfiguredTableByCampaignProperties = (
  p: MakeEmptyTableByCampaignConfig,
  element: ApiDashboardElement
): TableByCampaignJSON => ({
  ...makeEmptyTableByCampaignProperties(p),
  x: element.properties.x as number,
  y: element.properties.y as number,
  w: element.properties.w as number,
  h: element.properties.h as number,
});

const toConfiguredTextProperties = (
  element: ApiDashboardElement
): TextJSON => ({
  ...makeEmptyTextProperties(),
  state_type: 'configured_Text',
  x: element.properties.x as number,
  y: element.properties.y as number,
  w: element.properties.w as number,
  h: element.properties.h as number,
});

const updatePlotConfiguration = (
  p: MakeEmptyPlotConfig,
  element: ApiDashboardElement,
  preserveUnconfigured: boolean
):
  | PlotJSON
  | TimeseriesJSON
  | ScatterPlotJSON
  | WindroseJSON
  | OmbrothermicJSON
  | HistogramJSON
  | EmptyPlotJSON => {
  if (element.element_type === 'plot') {
    if ('state_type' in element.properties) {
      if (element.properties.state_type === 'configured_plot') {
        return element.properties;
      } else {
        if (preserveUnconfigured) {
          return element.properties;
        } else {
          return toConfiguredPlotProperties(p, element);
        }
      }
    } else {
      return element.properties;
    }
  } else {
    return makeEmptyPlotProperties(p);
  }
};

const updateTableConfiguration = (
  p: MakeEmptyTableConfig,
  element: ApiDashboardElement,
  preserveUnconfigured: boolean
): TableJSON | EmptyTableJSON => {
  if (element.element_type === 'table') {
    if ('state_type' in element.properties) {
      if (element.properties.state_type === 'configured_table') {
        return element.properties;
      } else {
        if (preserveUnconfigured) {
          return element.properties;
        } else {
          return toConfiguredTableProperties(p, element);
        }
      }
    } else {
      return element.properties;
    }
  } else {
    return makeEmptyTableProperties(p);
  }
};

const updateTableByCampaignConfiguration = (
  p: MakeEmptyTableConfig,
  element: ApiDashboardElement,
  preserveUnconfigured: boolean
): TableByCampaignJSON | EmptyTableByCampaignJSON => {
  if (element.element_type === 'tablebycampaign') {
    if ('state_type' in element.properties) {
      if (element.properties.state_type === 'configured_table_by_campaign') {
        return element.properties;
      } else {
        if (preserveUnconfigured) {
          return element.properties;
        } else {
          return toConfiguredTableByCampaignProperties(p, element);
        }
      }
    } else {
      return element.properties;
    }
  } else {
    return makeEmptyTableByCampaignProperties(p);
  }
};

const updateTextConfiguration = (
  element: ApiDashboardElement,
  preserveUnconfigured: boolean
): TextJSON | EmptyTextJSON => {
  if (element.element_type === 'text') {
    if ('state_type' in element.properties) {
      if (element.properties.state_type === 'configured_Text') {
        return element.properties;
      } else {
        if (preserveUnconfigured) {
          return element.properties;
        } else {
          return toConfiguredTextProperties(element);
        }
      }
    } else {
      return element.properties;
    }
  } else {
    return makeEmptyTextProperties();
  }
};

export const toPlot = (
  p: ToPlotConfig,
  element: ApiDashboardElement,
  preserveUnconfigured = false
): Plot => {
  const plotType: PlotTypes = 'timeseries';

  if (isPlot(element)) {
    return {
      ...element,
      properties: updatePlotConfiguration(
        { plot_type: element.properties.plot_type, ...p },
        element,
        preserveUnconfigured
      ),
    };
  }

  return {
    id: element.id,
    name: element.name,
    element_type: 'plot',
    data_source: isWithMeasurePoint(element) ? element.data_source : [],
    properties: updatePlotConfiguration(
      { plot_type: plotType, ...p },
      element,
      preserveUnconfigured
    ),
    resource_ids: null,
  };
};

// TODO: write and integrate to setPlotType
export const toHistogram = (
  element: ApiDashboardElement
): ApiDashboardElement => {
  if (!isPlot(element)) return element;
  if (isHistogram(element)) return element;
  const originalProperties = element.properties;
  return {
    ...element,
    properties: {
      ...originalProperties,
      plot_type: 'bar',
      state_type: 'configured_plot',
      data_source_to_axis: {},
      intervals: [
        'start_date' in originalProperties && 'end_date' in originalProperties
          ? {
              type: 'fixed_time_range',
              name: '',
              campaign_ids: null,
              startdate: originalProperties.start_date!,
              enddate: originalProperties.end_date!,
            }
          : {
              type: 'fixed_time_range',
              name: '',
              campaign_ids: null,
              startdate: new Date().toISOString(),
              enddate: new Date().toISOString(),
            },
      ],
    },
  };
};

export const toTimeseries = (
  element: ApiDashboardElement
): ApiDashboardElement => {
  if (!isPlot(element)) return element;
  if (isTimeseries(element)) return element;
  const newElement = toHistogram(element);
  return {
    ...(newElement as Timeseries),
    properties: {
      ...(newElement.properties as TimeseriesJSON),
      plot_type: 'timeseries',
    },
  };
};

export const toScatterPlot = (
  element: ApiDashboardElement
): ApiDashboardElement => {
  if (!isPlot(element)) return element;
  if (isScatterPlot(element)) return element;
  const newElement = toHistogram(element);
  return {
    ...(newElement as ScatterPlot),
    properties: {
      ...(newElement.properties as ScatterPlotJSON),
      plot_type: 'scatter',
    },
  };
};

/**
 * Ensures a plot element uses the configured scatter shape so scatter-only
 * mutators (additional_properties, intervals, etc.) can run during form edits.
 */
export function ensureConfiguredScatterPlot(
  element: ApiDashboardElement
): ApiDashboardElement {
  if (!isPlot(element)) {
    return element;
  }

  return isScatterPlot(element) ? element : toScatterPlot(element);
}

/**
 * Ensures a plot element uses the configured timeseries shape so timeseries-only
 * mutators (additional_properties, intervals, etc.) can run during form edits.
 */
export function ensureConfiguredTimeseries(
  element: ApiDashboardElement
): ApiDashboardElement {
  if (!isPlot(element)) {
    return element;
  }

  return isTimeseries(element) ? element : toTimeseries(element);
}

/**
 * Ensures a plot element uses the configured histogram shape so interval and
 * data-source mutators can run during form edits.
 */
export function ensureConfiguredHistogram(
  element: ApiDashboardElement
): ApiDashboardElement {
  if (!isPlot(element)) {
    return element;
  }

  return isHistogram(element) ? element : toHistogram(element);
}

/**
 * Ensures a plot element uses the configured windrose shape so interval and
 * data-source mutators can run during form edits.
 */
export function ensureConfiguredWindrose(
  element: ApiDashboardElement
): ApiDashboardElement {
  if (!isPlot(element)) {
    return element;
  }

  return isWindrose(element) ? element : toWindrose(element);
}

/**
 * Ensures a plot element uses the configured ombrothermic shape so interval and
 * data-source mutators can run during form edits.
 */
export function ensureConfiguredOmbrothermic(
  element: ApiDashboardElement
): ApiDashboardElement {
  if (!isPlot(element)) {
    return element;
  }

  return isOmbrothermic(element) ? element : toOmbrothermic(element);
}

/**
 * Ensures a table element uses the configured table shape so interval, column,
 * and aggregation mutators can run during form edits.
 */
export function ensureConfiguredTable(
  element: ApiDashboardElement,
  scope?: Pick<ToTableConfig, 'start_date' | 'end_date'>
): ApiDashboardElement {
  if (!isTable(element)) {
    return element;
  }

  if (isConfiguredTable(element)) {
    return element;
  }

  const start_date =
    scope?.start_date ?? getStartDate(element) ?? new Date().toISOString();
  const end_date =
    scope?.end_date ?? getEndDate(element) ?? new Date().toISOString();

  return toTable({ start_date, end_date }, element, false);
}

/**
 * Ensures a table-by-campaign element uses the configured shape so date-range
 * and data-source mutators can run during form edits.
 */
export function ensureConfiguredTableByCampaign(
  element: ApiDashboardElement,
  scope?: Pick<ToTableByCampaignConfig, 'start_date' | 'end_date'>
): ApiDashboardElement {
  if (!isTableByCampaign(element)) {
    return element;
  }

  if (isConfiguredTableByCampaign(element)) {
    return element;
  }

  const start_date =
    scope?.start_date ?? getStartDate(element) ?? new Date().toISOString();
  const end_date =
    scope?.end_date ?? getEndDate(element) ?? new Date().toISOString();

  return toTableByCampaign({ start_date, end_date }, element, false);
}

export const toWindrose = (
  element: ApiDashboardElement
): ApiDashboardElement => {
  if (!isPlot(element)) return element;
  if (isWindrose(element)) return element;
  const newElement = toHistogram(element);
  return {
    ...(newElement as Windrose),
    properties: {
      ...(newElement.properties as WindroseJSON),
      plot_type: 'windrose',
    },
  };
};

export const toOmbrothermic = (
  element: ApiDashboardElement
): ApiDashboardElement => {
  if (!isPlot(element)) return element;
  if (isOmbrothermic(element)) return element;
  const newElement = toHistogram(element);
  return {
    ...(newElement as Ombrothermic),
    properties: {
      ...(newElement.properties as OmbrothermicJSON),
      plot_type: 'ombrothermic',
    },
  };
};

export const toMap = (element: ApiDashboardElement): Map => {
  return {
    id: element.id,
    name: element.name,
    element_type: 'map',
    //TODO: check datasource type of original element.
    data_source: element.data_source as SelectionSchemaProjectSite[],
    properties: makeEmptyMapProperties('site'),
    resource_ids: [],
  };
};

// TODO: generate columns from data source
export const toTable = (
  p: ToTableConfig,
  element: ApiDashboardElement,
  preserveUnconfigured = false
): Table => {
  if (isTable(element)) {
    return {
      ...element,
      properties: updateTableConfiguration(p, element, preserveUnconfigured),
    };
  }

  return {
    id: element.id,
    name: element.name,
    data_source: isWithMeasurePoint(element) ? element.data_source : [],
    element_type: 'table',
    properties: updateTableConfiguration(p, element, preserveUnconfigured),
    resource_ids: null,
  };
};

interface ToTableByCampaignConfig {
  start_date: string;
  end_date: string;
}

export const toTableByCampaign = (
  p: ToTableByCampaignConfig,
  element: ApiDashboardElement,
  preserveUnconfigured = false
): TableByCampaign => {
  if (isTableByCampaign(element)) {
    return {
      ...element,
      properties: updateTableByCampaignConfiguration(
        p,
        element,
        preserveUnconfigured
      ),
    };
  }

  return {
    id: element.id,
    name: element.name,
    data_source: isWithMeasurePoint(element) ? element.data_source : [],
    element_type: 'tablebycampaign',
    properties: updateTableByCampaignConfiguration(
      p,
      element,
      preserveUnconfigured
    ),
    resource_ids: null,
  };
};

export const toText = (
  element: ApiDashboardElement,
  preserveUnconfigured = false
): Text => {
  return {
    id: element.id,
    name: element.name,
    element_type: 'text',
    data_source: element.data_source as {
      project_id: number;
      site_id: number;
      measure_type_id: number;
    }[],
    properties: updateTextConfiguration(element, preserveUnconfigured),
    resource_ids: null,
  };
};

export const toTextFromEntityProperty = (
  p: MakeTextFromEntityPropertyConfig,
  element: ApiDashboardElement
): TextFromEntityProperty => {
  return {
    id: element.id,
    name: element.name,
    element_type: 'textfromentityproperty',
    data_source: element.data_source as {
      project_id: number;
      site_id: number;
      measure_type_id: number;
    }[],
    properties: makeEmptyTextFromEntityPropertyProperties(p),
    resource_ids: null,
  };
};

export const toLocalizationFromCoordinates = (
  element: ApiDashboardElement
): LocalizationFromCoordinates => {
  return {
    id: element.id,
    name: element.name,
    element_type: 'localizationfromcoordinates',
    data_source: isWithProjectSite(element) ? element.data_source : [],
    properties: makeEmptyLocalizationFromCoordinatesProperties({
      coordinate_source: 'site',
    }),
    resource_ids: null,
  };
};

export const toImage = (element: ApiDashboardElement): Image => {
  return {
    id: element.id,
    name: element.name,
    element_type: 'image',
    data_source: isWithProjectSite(element) ? element.data_source : [],
    properties: makeEmptyImageProperties(),
    resource_ids: [],
  };
};

export const toImageGallery = (element: ApiDashboardElement): ImageGallery => {
  return {
    id: element.id,
    name: element.name,
    element_type: 'imagegallery',
    data_source: isWithProjectSite(element) ? element.data_source : [],
    properties: makeEmptyImageGalleryProperties(),
    resource_ids: [],
  };
};

export interface ToRemoteWidgetParams {
  specificWidgetType: string;
  url: string;
}

export const toRemoteWidget = (
  element: ApiDashboardElement,
  params: ToRemoteWidgetParams
): RemoteWidget => {
  return {
    id: element.id,
    name: element.name,
    element_type: 'remotewidget',
    data_source: [],
    properties: makeEmptyRemoteWidgetProperties(
      params.specificWidgetType,
      params.url
    ),
    resource_ids: null,
  };
};

// -- Getters and Setters

export function getId(element: ApiDashboardElement): number {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return elementIdM.get(element)!;
}

export function setId(
  id: number,
  element: ApiDashboardElement
): ApiDashboardElement {
  return elementIdM.set(id, element);
}

export function getName(element: ApiDashboardElement): string | undefined {
  return elementNameM.get(element);
}

export function setName(
  name: string,
  element: ApiDashboardElement
): ApiDashboardElement {
  return elementNameM.set(name, element);
}

export function getTextValue(
  element: ApiDashboardElement
): string | null | undefined {
  return guardedTextValueM.get(element);
}

export function setTextValue(
  value: string,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedTextValueM.set(value, element);
}

export function getElementType(
  element: ApiDashboardElement
): ApiDashboardElement['element_type'] | undefined {
  return elementTypeM.get(element);
}

interface SetElementTypeSignature {
  (
    element_type: 'plot',
    element: ApiDashboardElement,
    preserveUnconfigured: boolean,
    p: ToPlotConfig
  ): ApiDashboardElement;
  (element_type: 'map', element: ApiDashboardElement): ApiDashboardElement;
  (
    element_type: 'table',
    element: ApiDashboardElement,
    preserveUnconfigured: boolean,
    p: ToTableConfig
  ): ApiDashboardElement;
  (
    element_type: 'tablebycampaign',
    element: ApiDashboardElement,
    preserveUnconfigured: boolean,
    p: ToTableByCampaignConfig
  ): ApiDashboardElement;
  (
    element_type: 'text',
    element: ApiDashboardElement,
    preserveUnconfigured: boolean
  ): ApiDashboardElement;
  (
    element_type: 'textfromentityproperty',
    element: ApiDashboardElement,
    p: MakeTextFromEntityPropertyConfig
  ): ApiDashboardElement;
  (
    element_type: 'localizationfromcoordinates',
    element: ApiDashboardElement
  ): ApiDashboardElement;
  (
    element_type: 'table',
    element: ApiDashboardElement,
    preserveUnconfigured: boolean,
    p: ToTableConfig
  ): ApiDashboardElement;
  (element_type: 'image', element: ApiDashboardElement): ApiDashboardElement;
  (
    element_type: 'remotewidget',
    element: ApiDashboardElement,
    preserveUnconfigured: boolean,
    p: ToRemoteWidgetParams
  ): ApiDashboardElement;
  (
    element_type: 'imagegallery',
    element: ApiDashboardElement
  ): ApiDashboardElement;
}

export const setElementType: SetElementTypeSignature = (
  element_type,
  element,
  preserveUnconfigured = false,
  p?
) => {
  switch (element_type) {
    case 'plot':
      return toPlot(
        p as any,
        element,
        preserveUnconfigured as boolean
      ) as ApiDashboardElement;
    case 'map':
      return toMap(element) as ApiDashboardElement;
    case 'table':
      return toTable(
        p as any,
        element,
        preserveUnconfigured as boolean
      ) as ApiDashboardElement;
    case 'tablebycampaign':
      return toTableByCampaign(
        p as any,
        element,
        preserveUnconfigured as boolean
      );
    case 'text':
      return toText(
        element,
        preserveUnconfigured as boolean
      ) as ApiDashboardElement;
    case 'textfromentityproperty':
      return toTextFromEntityProperty(p as any, element) as ApiDashboardElement;
    case 'localizationfromcoordinates':
      return toLocalizationFromCoordinates(element) as ApiDashboardElement;
    case 'image':
      return toImage(element) as ApiDashboardElement;
    case 'remotewidget':
      return toRemoteWidget(element, p as any) as ApiDashboardElement;
    case 'imagegallery':
      return toImageGallery(element) as ApiDashboardElement;
  }
};

export function getSpecificWidgetType(
  element: ApiDashboardElement
): string | undefined {
  return guardedSpecificWidgetTypeM.get(element);
}

export function setSpecificWidgetType(
  specificWidgetType: string,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedSpecificWidgetTypeM.set(specificWidgetType, element);
}

export function getUrl(element: ApiDashboardElement): string | undefined {
  return guardedUrlM.get(element);
}

export function setUrl(
  url: string,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedUrlM.set(url, element);
}

export function getAdditionalProperties(element: ApiDashboardElement) {
  return guardedAdditionalPropertiesM.get(element);
}

export function setAdditionalProperties(
  specificWidgetType: JSONSerializable,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedAdditionalPropertiesM.set(specificWidgetType, element);
}

export const REMOTE_WIDGET_AWAITING_CONFIGURATION = {
  awaitingConfiguration: true,
} as const;

/** Only the exact sentinel object means awaiting; not null, undefined, or (accidentally) mixed props. */
export function isRemoteWidgetAwaitingConfiguration(
  additionalProps: JSONSerializable | null | undefined
): boolean {
  if (additionalProps === null || additionalProps === undefined) return false;
  if (typeof additionalProps !== 'object' || Array.isArray(additionalProps)) {
    return false;
  }
  const keys = Object.keys(additionalProps);
  return (
    keys.length === 1 &&
    (additionalProps as { awaitingConfiguration?: boolean }).awaitingConfiguration ===
      true
  );
}

/** Removes the internal awaitingConfiguration key; collapses sentinel-only objects to null. */
export function stripAwaitingConfigurationProperty(
  additionalProps: JSONSerializable | null | undefined
): JSONSerializable | null | undefined {
  if (additionalProps === null || additionalProps === undefined) {
    return additionalProps;
  }
  if (typeof additionalProps !== 'object' || Array.isArray(additionalProps)) {
    return additionalProps;
  }
  if (!('awaitingConfiguration' in additionalProps)) {
    return additionalProps;
  }
  const { awaitingConfiguration: _awaiting, ...rest } = additionalProps as Record<
    string,
    JSONSerializable
  >;
  if (Object.keys(rest).length === 0) {
    return null;
  }
  return rest as JSONSerializable;
}

/** Strips the awaiting-configuration sentinel before passing props to plugin UI. */
export function getAdditionalPropertiesForRemotePlugin(
  additionalProps: JSONSerializable | null | undefined
): JSONSerializable | null | undefined {
  return stripAwaitingConfigurationProperty(additionalProps);
}

/** Returns a config copy safe to pass to remote plugin Form/ConfigPanel (never includes the sentinel). */
export function sanitizeRemoteWidgetConfigForPlugin(
  element: ApiDashboardElement
): ApiDashboardElement {
  if (!isRemoteWidget(element)) return element;
  const raw = getAdditionalProperties(element) as JSONSerializable | null | undefined;
  const sanitized = stripAwaitingConfigurationProperty(raw);
  if (sanitized === raw) return element;
  return setAdditionalProperties(sanitized ?? null, element);
}

/** Strips the awaiting-configuration sentinel when the config panel form is submitted. */
export function finishRemoteWidgetConfiguration(
  element: ApiDashboardElement
): ApiDashboardElement {
  if (!isRemoteWidget(element)) return element;
  const before = getAdditionalProperties(element) as
    | JSONSerializable
    | null
    | undefined;
  const stripped = stripAwaitingConfigurationProperty(before);
  if (stripped === before) return element;
  return setAdditionalProperties(stripped ?? null, element);
}

/** Prefers the latest config when a stale override still carries the awaiting sentinel. */
export function resolveRemoteWidgetConfigForSubmit(
  latest: ApiDashboardElement,
  override?: ApiDashboardElement
): ApiDashboardElement {
  if (override === undefined) return latest;
  const overrideProps = getAdditionalProperties(override) as
    | JSONSerializable
    | null
    | undefined;
  if (isRemoteWidgetAwaitingConfiguration(overrideProps)) {
    return latest;
  }
  return override;
}

export function getDataSourcesMeasurePoint(
  element: WithMeasurePoint
): WithMeasurePoint['data_source'] | undefined {
  return dataSourcesWithMeasurePointM.get(element);
}

export function getDataSourcesProjectSite(
  element: WithProjectSite
): WithProjectSite['data_source'] | undefined {
  return dataSourcesWithProjectSiteM.get(element);
}

export function getDataSourcesAnySelection(
  element: WithAnySelection
): WithAnySelection['data_source'] | undefined {
  return dataSourcesWithAnySelectionM.get(element);
}

export function isTimelinePlot(element: ApiDashboardElement): boolean {
  return (
    getPlotType(element) === 'timeseries' ||
    getPlotType(element) === 'ombrothermic'
  );
}

function isHistogramPlot(element: ApiDashboardElement): boolean {
  return getPlotType(element) === 'bar';
}

export function getIntervals(
  element: ApiDashboardElement
): IntervalJSON[] | undefined {
  return guardedIntervalsM.get(element);
}

export function setIntervals(
  value: IntervalJSON[],
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedIntervalsM.set(value, element);
}

export function updateIntervals(
  f: (v: IntervalJSON[]) => IntervalJSON[],
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedIntervalsM.update(f, element);
}

/**
 * Sets measure-point data sources using a pre-resolved label map (no association graph).
 */
const makeAxisFromLabelMap =
  (mtid2unit: MeasureTypeIdToUnit, sourceLabelMap: SourceMap) =>
  (
    axis_type: 'x' | 'y' | 'r' | 'theta' | 'phi',
    ds: SelectionSchemaMeasurePoint,
    _pathGroup: SelectionSchemaMeasurePoint[]
  ): AxisJSON => ({
    axisName: getSourceLabelFromMap(sourceLabelMap, ds) ?? 'Mesure',
    start: 0,
    end: 5000,
    axis_type,
    unit: mtid2unit[ds.measure_type_id] ?? '-',
  });

/** Sets measure-point data sources using a pre-resolved label map (no association graph). */
export function makeSetDataSourcesFromLabelMap(
  mtid2unit: MeasureTypeIdToUnit,
  sourceLabelMap: SourceMap
): (
  dataSources: (SelectionSchemaMeasurePoint | SelectionSchemaProjectSite)[],
  element: ApiDashboardElement
) => ApiDashboardElement {
  const maker = makeAxisFromLabelMap(mtid2unit, sourceLabelMap);
  return (dataSources, element) => {
    if (isWithMeasurePoint(element)) {
      if (!areAllPathsWithMeasurePoint(dataSources)) {
        return element;
      }
      const postChange = (
        data: WithMeasurePoint,
        dataSources: SelectionSchemaMeasurePoint[]
      ) => {
        const newAxes = A.toRecord(
          dataSources.map((ds, i, dataSources) =>
            maker(
              isTimelinePlot(element) || isHistogramPlot(element)
                ? 'y'
                : i === dataSources.length - 1
                ? 'x'
                : 'y',
              ds,
              dataSources
            )
          )
        );
        const withUpdatedAxes = guardedDataSourceToAxisM.set(newAxes, data);
        const maxDSIndex = dataSources.length - 1;
        const withUpdatedDataSeriesColumns = updateDataSeriesColumnsE(
          withUpdatedAxes,
          (columns) =>
            columns.filter((col) => col.data_source_index <= maxDSIndex)
        );
        return withUpdatedDataSeriesColumns as WithMeasurePoint;
      };
      const modifier = addTrigger(
        dataSourcesWithMeasurePointM,
        postChange,
        (d) => d
      );
      const modified = modifier.set(
        dataSources as SelectionSchemaMeasurePoint[],
        element
      );
      return modified;
    } else if (isWithProjectSite(element)) {
      if (!areAllPathsWithProjectSite(dataSources)) {
        return element;
      }
      const modifier = dataSourcesWithProjectSiteM;
      const modified = modifier.set(
        dataSources as SelectionSchemaProjectSite[],
        element
      );
      return modified;
    } else if (isWithAnySelection(element)) {
      const modifier = dataSourcesWithAnySelectionM;
      const modified = modifier.set(dataSources, element);
      return modified;
    }
    throw new Error(
      `Unhandled type ${element.element_type} to change datasources`
    );
  };
}

export function makeSetDataSourcesWithProjectSite(): (
  dataSources: SelectionSchemaProjectSite[],
  element: WithProjectSite
) => ApiDashboardElement {
  return (dataSources, element) => {
    const modified = dataSourcesWithProjectSiteM.set(dataSources, element);
    return modified;
  };
}

export function getPlotType(
  element: ApiDashboardElement
): PlotTypes | undefined {
  return guardedPlotTypeM.get(element);
}

export function setPlotType(
  plotType: PlotTypes,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedDataSourceToAxisM.set(
    {},
    guardedDataSourcesM.set([], guardedPlotTypeM.set(plotType, element))
  );
}

export type PlotTypeToPossibleDataSources = {
  [k in PlotTypes]: Record<number, Record<number, number[]>>;
};

export function getDataSourceToAxis(
  element: ApiDashboardElement
): Record<number, AxisJSON> | undefined {
  return guardedDataSourceToAxisM.get(element);
}

export function getCoordinateSource(
  element: ApiDashboardElement
): Map['properties']['coordinate_source'] | undefined {
  return guardedCoordinateSourceM.get(element);
}

export function setCoordinateSource(
  coordinateSource: Map['properties']['coordinate_source'],
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedCoordinateSourceM.set(coordinateSource, element);
}

export function getEntityProperty(
  element: ApiDashboardElement
): TextFromEntityProperty['properties']['entity_property'] | undefined {
  return guardedEntityPropertyM.get(element);
}

export function getEntityId(
  element: ApiDashboardElement
): TextFromEntityProperty['properties']['entity_id'] | undefined {
  return guardedEntityId.get(element);
}

export function getEntityType(
  element: ApiDashboardElement
): TextFromEntityProperty['properties']['entity_type'] | undefined {
  return guardedEntityType.get(element);
}

export function getPossibleEntityIds(
  entity_type: 'project' | 'site',
  element: WithProjectSite
): number[] {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dataSources = getDataSourcesProjectSite(element)!;
  return dataSources.map(
    (ds) => ds[(entity_type + '_id') as keyof Omit<typeof ds, 'group_index'>]
  );
}

export function getElements(
  element: ApiDashboardElement
): ApiDashboardElement[] {
  return guardedElementsM.get(element) ?? [];
}

export function setElements(
  elements: ApiDashboardElement[],
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedElementsM.set(elements as Collection['elements'], element);
}

export function getPossibleEntityProperties(
  entity_type: 'project' | 'site'
): string[] {
  return entity_type === 'project' ? projectKeyWithString : siteKeyWithString;
}

type KeysMatchingType<T, TargetType> = {
  [K in keyof T]-?: T[K] extends TargetType ? K : never;
}[keyof T];

type ProjectStringValues = KeysMatchingType<ApiReturnProject, string>;
type SiteStringValues = KeysMatchingType<ApiReturnSite, string>;

const projectKeyWithString: ProjectStringValues[] = ['name'];
const siteKeyWithString: SiteStringValues[] = ['name'];

export function setEntityType(
  entityType: 'project' | 'site',
  element: WithProjectSite
): ApiDashboardElement {
  const postChange = (data: ApiDashboardElement) => {
    if (!isWithProjectSite(element)) {
      return element;
    }
    const possibleIds = getPossibleEntityIds(
      entityType,
      data as WithProjectSite
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const firstId = possibleIds[0]!;
    const possibleProperties =
      entityType === 'project' ? projectKeyWithString : siteKeyWithString;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const firstProperty = possibleProperties[0]!;
    return guardedEntityPropertyM.set(
      firstProperty,
      guardedEntityId.set(firstId, guardedEntityType.set(entityType, data))
    );
  };
  const modifier = addTrigger(guardedEntityType, postChange, (d) => d);
  return modifier.set(entityType, element);
}

export function setEntityId(
  entityId: number,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedEntityId.set(entityId, element);
}

export function setEntityProperty(
  entityProperty: string,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedEntityPropertyM.set(entityProperty, element);
}

export function getStartDate(element: ApiDashboardElement): string | undefined {
  return guardedStartDateM.get(element);
}

export function setStartDate(
  startDate: string,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedStartDateM.set(startDate, element);
}

export function getEndDate(element: ApiDashboardElement): string | undefined {
  return guardedEndDateM.get(element);
}

export function setEndDate(
  endDate: string,
  element: ApiDashboardElement
): ApiDashboardElement {
  return guardedEndDateM.set(endDate, element);
}

export function setStartDateAndEndDate(
  startDate: string,
  endDate: string,
  element: ApiDashboardElement
): ApiDashboardElement {
  return setStartDate(startDate, setEndDate(endDate, element));
}

export const setLayout = (
  layout: { x: number; y: number; w: number; h: number },
  element: ApiDashboardElement
): ApiDashboardElement => {
  return {
    ...element,
    properties: {
      ...element.properties,
      x: layout.x,
      y: layout.y,
      w: layout.w,
      h: layout.h,
    },
  } as ApiDashboardElement;
};

// -- builders

export function dashboardFromDashboardElement(
  element: ApiDashboardElement
): ApiDashboardParametersType {
  return {
    id: element.id,
    name: element.name,
    elements: [element],
    shared: false,
    type: 'user_dashboard',
    masked: false,
    additional_properties: {
      cols: { lg: 24, md: 18, sm: 12, xs: 9, xxs: 6 },
      rowHeight: 16,
      margin: [6, 6],
    },
  };
}

// -- validators

const hasValidDataSources: ValidationFunction<
  ApiDashboardElement,
  'dataSource'
> = (element) => {
  let valid = false;
  let messages: string[] = [];
  let lowerDataSourceBoundary = 0;
  switch (element.element_type) {
    case 'plot':
      if (!isConfiguredPlot(element)) {
        return [true, { dataSource: [] }];
      }
      lowerDataSourceBoundary = getPlotType(element) === 'scatter' ? 1 : 0;
      valid =
        getDataSourcesMeasurePoint(element)!.length > lowerDataSourceBoundary;
      messages = valid ? [] : ['Au moins une source de donnée'];
      return [valid, { dataSource: messages }];
    case 'map':
      valid = getDataSourcesProjectSite(element)!.length > 0;
      messages = valid ? [] : ['Au moins une source de donnée'];
      return [valid, { dataSource: messages }];
    case 'table':
      if (!isConfiguredTable(element)) {
        return [true, { dataSource: [] }];
      }
      valid = getDataSourcesMeasurePoint(element)!.length > 0;
      messages = valid ? [] : ['Au moins une source de donnée'];
      return [valid, { dataSource: messages }];
    case 'tablebycampaign':
      if (!isConfiguredTableByCampaign(element)) {
        return [true, { dataSource: [] }];
      }
      valid = getDataSourcesMeasurePoint(element)!.length > 0;
      messages = valid ? [] : ['Au moins une source de donnée'];
      return [valid, { dataSource: messages }];
    case 'text':
      valid = getDataSourcesAnySelection(element)!.length === 0;
      messages = valid ? [] : ['Aucune source de donnée ne doit être assignée'];
      return [valid, { dataSource: messages }];
    case 'textfromentityproperty':
      valid = getDataSourcesProjectSite(element)!.length > 0;
      messages = valid ? [] : ['Au moins une source de donnée'];
      return [valid, { dataSource: messages }];
    case 'localizationfromcoordinates':
      valid = getDataSourcesProjectSite(element)!.length > 0;
      messages = valid ? [] : ['Au moins une source de donnée'];
      return [valid, { dataSource: messages }];
    case 'image':
      valid = getDataSourcesProjectSite(element)!.length === 1;
      messages = valid
        ? []
        : ['Une et une seule source de donnée doit être assignée'];
      return [valid, { dataSource: messages }];
    case 'imagegallery':
      valid = getDataSourcesProjectSite(element)!.length === 1;
      messages = valid
        ? []
        : ['Une et une seule source de donnée doit être assignée'];
      return [valid, { dataSource: messages }];
    case 'remotewidget':
      valid = true;
      messages = [];
      return [valid, { dataSource: messages }];
    default:
      valid = false;
      messages = ['Règle de validation inconnue'];
      return [valid, { dataSource: messages }];
  }
};

const hasNonEmptyName: ValidationFunction<ApiDashboardElement, 'name'> = (
  element
) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const name = elementNameM.get(element)!;
  const valid = name !== '';
  const messages = valid ? [] : ['Le nom est requis'];
  return [valid, { name: messages }];
};

const hasLongEnoughName: ValidationFunction<ApiDashboardElement, 'name'> = (
  element
) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const name = elementNameM.get(element)!;
  const valid = name.length >= 3;
  const messages = valid ? [] : ['au moins 3 caractères'];
  return [valid, { name: messages }];
};

const hasNonWhiteSpaceName: ValidationFunction<ApiDashboardElement, 'name'> = (
  element
) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const name = elementNameM.get(element)!;
  const valid = !/^\s+$/.test(name);
  const messages = valid ? [] : ['Ne peut pas être juste des espaces'];
  return [valid, { name: messages }];
};

const hasValidDateRange: ValidationFunction<
  ApiDashboardElement,
  'dateRange'
> = (element) => {
  const startDate = guardedStartDateM.get(element);
  const endDate = guardedEndDateM.get(element);
  if (
    startDate === new Date(0).toISOString() ||
    endDate === new Date(0).toISOString()
  ) {
    const valid = false;
    const messages = ['La date de début et la date de fin sont requises'];
    return [valid, { dateRange: messages }];
  }
  return [true, {}];
};

export const hasValidRow: ValidationFunction<ApiDashboardElement, 'rows'> = (
  element: ApiDashboardElement
) => {
  if (!isTable(element) || !isConfiguredTable(element)) {
    return [true, {}];
  }

  const config = element.properties.config;
  if (config.type !== 'AggregatedTable') {
    return [true, {}];
  }

  const hasAggregatedColumns = config.rows.some((row) =>
    row.columns.some(isAggregatedColumn)
  );

  if (config.rows.length === 0 || !hasAggregatedColumns) {
    return [
      false,
      {
        rows: ["Au moins un type d'agrégation doit être sélectionné"],
      },
    ];
  }

  return [true, {}];
};

export const isValid = validationAll(
  hasValidDataSources,
  hasNonEmptyName,
  hasLongEnoughName,
  hasNonWhiteSpaceName,
  hasValidDateRange,
  hasValidRow
);

// -- Retreival from store

export function getAxesSelectionFromStore(
  displayerId: number,
  store: RootState
): { elementId: number; data: AxesSelection } | undefined {
  const dashboardSlice = getDashboardSlice(store);
  const widget = findWidgetByDisplayerIdAcrossGrids(
    displayerId,
    dashboardSlice
  );

  if (!widget) return undefined;

  const element = widget.elementData;
  const elementId = widget.dashboardElementId;
  const start_date = getStartDate(element);
  const end_date = getEndDate(element);

  if (!start_date || !end_date) return undefined;

  const data_source_to_axis = isWithDataSourceToAxis(element)
    ? dataSourceToAxisM.get(element) ?? {}
    : {};

  return {
    elementId,
    data: {
      data_source: element.data_source,
      start_date,
      end_date,
      data_source_to_axis,
    },
  };
}
